import type { AnalyticsField } from '../analytics/AnalyticsTypes';
import { ASSIST_FIELDS, type AssistFieldDef } from './AssistContract.js';

/**
 * Bridges the curated static analytics vocabulary ({@link ASSIST_FIELDS}) with the
 * tenant's dynamic, context-service classifier dimensions so every assist surface
 * (composer, AMA, Add-insight modal, filter editor) can offer — and validate
 * against — the full set of fields. Pure + runtime-light, so both the tsc-built
 * BFF and the Vite-built frontend import it. The dynamic dimensions are fetched
 * per-runtime (BFF over the context API; frontend via the context client) and
 * merged here; if none are available, the merge is just the static set, so every
 * surface degrades to today's behavior.
 */

/** A tenant-defined classifier dimension: its OLAP `dimensions['key']` map key plus admin/LLM-facing copy. */
export interface DynamicDimension {
    key: string;
    label: string;
    description: string;
}

const STATIC_VALUES = new Set<string>(ASSIST_FIELDS.map((field) => field.value));

/** Merges the static fields with the dynamic dimensions (deduped by key; static wins). */
export function mergeFieldDefs(dynamic: DynamicDimension[]): AssistFieldDef[] {
    const seen = new Set<string>(STATIC_VALUES);
    const extra: AssistFieldDef[] = [];
    for (const dim of dynamic) {
        if (dim.key.length === 0 || seen.has(dim.key)) continue;
        seen.add(dim.key);
        extra.push({
            value: dim.key as AnalyticsField,
            label: dim.label.length > 0 ? dim.label : dim.key,
            description: dim.description.length > 0 ? dim.description : 'A tenant-defined classified dimension.',
        });
    }
    return [...ASSIST_FIELDS, ...extra];
}

/**
 * A neutral, stable presentation order for the model: alphabetical by label, so
 * the curated static fields and the tenant's dynamic dimensions interleave rather
 * than the dynamic ones always trailing the static set. The model sees the field
 * vocabulary ONLY through this order (the prose catalog), so no field is
 * privileged by position. Deterministic, so the system prompts stay cache-stable
 * across requests; UI ordering and validation are unaffected.
 */
function orderedForModel(fields: AssistFieldDef[]): AssistFieldDef[] {
    return [...fields].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The shape of a usable field key: a base column or a `dimensions` map key.
 * Strictly narrower than the OLAP query compiler's `dimKey` guard (same charset,
 * plus a 64-char cap as an extra bound on model output), so any key this admits
 * compiles safely — an absent key just reads as NULL. The model is NOT restricted
 * to the curated catalog: any tenant dimension key it has confirmed in the data
 * is fair game, which is why validation is by shape, not by catalog membership.
 */
const FIELD_KEY_PATTERN = /^[A-Za-z0-9_]{1,64}$/;

/** Whether a value is a usable field key (see {@link FIELD_KEY_PATTERN}). */
export function isFieldKey(value: unknown): value is string {
    return typeof value === 'string' && FIELD_KEY_PATTERN.test(value);
}

/** The model-facing catalog text: one `- value: label — description` line per field. */
export function fieldCatalogText(fields: AssistFieldDef[]): string {
    return orderedForModel(fields)
        .map((field) => `- ${field.value}: ${field.label} — ${field.description}`)
        .join('\n');
}

/** A JSON schema for an array of `{ field, values }` filters. The field is an open key (any known field or confirmed dimension key), validated server-side by {@link isFieldKey}. */
export function filtersSchema(): Record<string, unknown> {
    return {
        type: 'array',
        items: {
            type: 'object',
            additionalProperties: false,
            properties: {
                field: { type: 'string', description: 'A field key: a known field below, or any dimension key confirmed in the data.' },
                values: { type: 'array', items: { type: 'string' }, minItems: 1 },
            },
            required: ['field', 'values'],
        },
    };
}
